/**
 * FinanceProTemplate.js
 * ─────────────────────────────────────────────────────────────────────────────
 * ATS Rating: 98 / 100 — ATS Verified
 * Style: Conservative, metrics-heavy, two-zone layout
 * Best For: Investment banking, CA, CFA, corporate finance, BFSI sector
 */

import React from 'react';
import EditableField from '../components/EditableField';
import {
  normalizeTemplateData, BulletList, SkillsBlock,
  ContactRow, EmptyState, DS,
} from './templateDesignSystem';

function FinanceProTemplate({
  resumeData,
  accentColor = '#92400e',
  fontScale = 1,
  editMode = false,
  onFieldUpdate = () => { },
  onDeleteBullet = null,
  onDeleteSkill = null,
}) {
  if (!resumeData) return <EmptyState />;
  const d = normalizeTemplateData(resumeData);
  const { name, role, summaryText, skills, experience, internships, projects, educationList, certList, achieveList, extraList, languageList } = d; // eslint-disable-line no-unused-vars
  const scale = fontScale || 1;
  const fs = (base) => `${parseFloat(base) * scale}px`;

  const FinSection = ({ title }) => (
    <div style={{ marginTop: 13, marginBottom: 6 }}>
      <h2 style={{
        fontSize: fs(DS.fs.section), fontWeight: 800, textTransform: 'uppercase',
        letterSpacing: '1.4px', color: accentColor,
        margin: 0, fontFamily: 'inherit',
        borderBottom: `1.5px solid ${accentColor}`,
        paddingBottom: 3,
      }}>{title}</h2>
    </div>
  );

  return (
    <div id="resume" style={{ background: '#fff', color: '#111', fontFamily: 'Times New Roman, Georgia, serif', fontSize: fs(DS.fs.body), lineHeight: 1.55, padding: `${DS.sp.pageY} ${DS.sp.pageX}`, boxSizing: 'border-box' }}>

      {/* ─── Header ─── */}
      <header style={{ marginBottom: 12 }}>
        <div style={{ textAlign: 'center', marginBottom: 8 }}>
          <EditableField editMode={editMode} value={name || ''} onSave={v => onFieldUpdate('name', v)} tag="h1"
            style={{ fontSize: fs('24px'), fontWeight: 900, color: '#0f172a', margin: 0, letterSpacing: '-0.3px', fontFamily: 'inherit' }} placeholder="Your Name" />
          {(role || editMode) && (
            <EditableField editMode={editMode} value={role || ''} onSave={v => onFieldUpdate('role', v)} tag="p"
              style={{ fontSize: fs('11px'), fontWeight: 700, color: accentColor, margin: '3px 0 0', letterSpacing: '0.5px', fontFamily: 'inherit' }} placeholder="Chartered Accountant / Finance Analyst" />
          )}
        </div>
        <div style={{ display: 'flex', justifyContent: 'center', borderTop: `2px solid ${accentColor}`, borderBottom: `2px solid ${accentColor}`, padding: '5px 0' }}>
          <ContactRow data={d} accentColor={accentColor} />
        </div>
      </header>

      {/* ─── Summary / Profile ─── */}
      {(summaryText || editMode) && (
        <section data-ats="summary" style={{ marginBottom: DS.sp.sectionMb }}>
          <FinSection title="Professional Profile" />
          <EditableField editMode={editMode} value={summaryText} onSave={v => onFieldUpdate('summary', v)}
            tag="p" multiline style={{ fontSize: fs(DS.fs.body), color: '#374151', lineHeight: 1.7, margin: 0, textAlign: 'justify', fontFamily: 'inherit' }} placeholder="Finance professional profile..." />
        </section>
      )}

      {/* ─── Core Competencies / Skills ─── */}
      {skills.length > 0 && (
        <section data-ats="skills" style={{ marginBottom: DS.sp.sectionMb }}>
          <FinSection title="Core Competencies" />
          <SkillsBlock skills={skills} accentColor={accentColor} layout="rows" />
        </section>
      )}

      {/* ─── Professional Experience ─── */}
      {experience.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <FinSection title="Professional Experience" />
          {experience.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`experience.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 800, color: '#0f172a', margin: 0, fontFamily: 'inherit' }} />
                  {exp.company && <span style={{ fontSize: fs(DS.fs.body), color: '#6B7280', fontWeight: 600, fontStyle: 'italic' }}>{exp.company}</span>}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`experience.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#6B7280', fontStyle: 'italic', whiteSpace: 'nowrap', marginLeft: 12, fontFamily: 'inherit' }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Internships / Articleship ─── */}
      {internships.length > 0 && (
        <section data-ats="experience" style={{ marginBottom: DS.sp.sectionMb }}>
          <FinSection title="Articleship / Internships" />
          {internships.map((exp, idx) => (
            <div key={idx} style={{ marginBottom: 10 }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                <div>
                  <EditableField editMode={editMode} value={exp.role || ''} onSave={v => onFieldUpdate(`internships.${idx}.role`, v)}
                    tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 800, color: '#0f172a', margin: 0, fontFamily: 'inherit' }} />
                  {exp.company && <span style={{ fontSize: fs(DS.fs.body), color: '#6B7280', fontStyle: 'italic' }}>{exp.company}</span>}
                </div>
                <EditableField editMode={editMode} value={exp.duration || ''} onSave={v => onFieldUpdate(`internships.${idx}.duration`, v)}
                  tag="span" style={{ fontSize: fs(DS.fs.small), color: '#6B7280', fontStyle: 'italic', whiteSpace: 'nowrap', marginLeft: 12 }} />
              </div>
              <BulletList points={exp.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Key Transactions / Projects ─── */}
      {projects.length > 0 && (
        <section data-ats="projects" style={{ marginBottom: DS.sp.sectionMb }}>
          <FinSection title="Key Transactions & Projects" />
          {projects.map((proj, idx) => (
            <div key={idx} style={{ marginBottom: DS.sp.itemMb }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
                <span style={{ fontSize: fs('10.5px'), fontWeight: 700, color: '#0f172a', fontFamily: 'inherit' }}>{proj.title || proj.name}</span>
                {proj.impact && <span style={{ fontSize: fs(DS.fs.small), color: '#059669', fontWeight: 700 }}>{proj.impact}</span>}
              </div>
              <BulletList points={proj.points} accentColor={accentColor} />
            </div>
          ))}
        </section>
      )}

      {/* ─── Education ─── */}
      {educationList.length > 0 && (
        <section data-ats="education" style={{ marginBottom: DS.sp.sectionMb }}>
          <FinSection title="Education & Qualifications" />
          {educationList.map((edu, idx) => (
            <div key={idx} style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 6 }}>
              <div>
                <EditableField editMode={editMode} value={edu?.degree || ''} onSave={v => onFieldUpdate(`education.${idx}.degree`, v)}
                  tag="h3" style={{ fontSize: fs('10.5px'), fontWeight: 800, color: '#0f172a', margin: 0, display: 'inline', fontFamily: 'inherit' }} />
                <span style={{ color: '#6B7280', fontSize: fs(DS.fs.body), fontStyle: 'italic', marginLeft: 6 }}>{edu?.college}</span>
                {edu?.cgpa && <span style={{ fontSize: fs(DS.fs.small), color: accentColor, fontWeight: 700, marginLeft: 6 }}>Score: {edu.cgpa}</span>}
              </div>
              <span style={{ fontSize: fs(DS.fs.small), color: '#9CA3AF', fontStyle: 'italic' }}>{edu?.year}</span>
            </div>
          ))}
        </section>
      )}

      {certList.length > 0 && (
        <section data-ats="certifications" style={{ marginBottom: DS.sp.sectionMb }}>
          <FinSection title="Certifications & Memberships" />
          <BulletList points={certList} accentColor={accentColor} />
        </section>
      )}
      {achieveList.length > 0 && (
        <section data-ats="achievements" style={{ marginBottom: DS.sp.sectionMb }}>
          <FinSection title="Awards & Recognition" />
          <BulletList points={achieveList} accentColor={accentColor} />
        </section>
      )}
    </div>
  );
}

export default FinanceProTemplate;
